import { useCallback, useEffect, useState } from "react";
import { Grid3X3 } from "lucide-react";
import { Select } from "antd";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import NineBoxGrid from "../../components/appraisal/NineBoxGrid";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import useAppraisalRole from "../../hooks/useAppraisalRole";
import { APPRAISAL_CYCLES, APPRAISAL_NINE_BOX, EMPLOYEE_DEPARTMENT } from "../../constants/api";

/**
 * Talent grid for a cycle.
 *
 * HR picks any scope; a reporting manager is narrowed to their own reports by
 * the API, so this page is safe to expose under team_appraisals without a
 * second client-side gate.
 */

/* Master endpoints go through tools.utils.paginator, which returns a bare list
   only when `drop_down` is set and a {result, total_count} object otherwise.
   Ask for the list, and unwrap defensively anyway -- a shape mismatch should
   degrade to an empty dropdown, not take the page down with it. */
const asList = (data) => (Array.isArray(data) ? data : data?.result || []);

const NineBoxView = () => {
    const { fetch } = useApiMethods();
    const { isHR } = useAppraisalRole();

    const [cycles, setCycles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [cycleId, setCycleId] = useState(null);
    const [departmentId, setDepartmentId] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetch(APPRAISAL_CYCLES)
            .then((res) => {
                if (cancelled) return;
                const rows = res?.data || [];
                setCycles(rows);
                // Default to the most recent cycle: nobody opens this page to
                // choose a cycle, they open it to look at the current one.
                if (rows.length) setCycleId(rows[0].id);
            })
            .catch(() => { if (!cancelled) ToastMsg("error", "Failed to load cycles"); });
        if (isHR) {
            fetch(EMPLOYEE_DEPARTMENT, { drop_down: "True" })
                .then((res) => { if (!cancelled) setDepartments(asList(res?.data)); })
                .catch(() => { });
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHR]);

    const load = useCallback(async () => {
        if (!cycleId) return;
        setLoading(true);
        try {
            const params = { cycle: cycleId };
            if (departmentId) params.department = departmentId;
            const res = await fetch(APPRAISAL_NINE_BOX, params);
            setData(res?.status === "success" ? res.data : null);
            if (res?.status !== "success") ToastMsg("error", res?.message || "Failed to load the grid");
        } catch (_) {
            setData(null);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cycleId, departmentId]);

    useEffect(() => { load(); }, [load]);

    const plotted = data?.plotted || [];
    const awaiting = data?.awaiting_potential || [];

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={Grid3X3}
                title="Nine-Box Grid"
                subtitle="Performance against potential"
                totalRecords={plotted.length}
            />

            <div className="px-3 pb-6 space-y-4">
                <div className="flex flex-wrap gap-3">
                    <div className="min-w-[220px]">
                        <label className="block text-xs font-medium mb-1">Cycle</label>
                        <Select
                            value={cycleId}
                            onChange={setCycleId}
                            className="w-full"
                            options={cycles.map((c) => ({ value: c.id, label: c.name }))}
                            placeholder="Select a cycle"
                        />
                    </div>
                    {isHR && (
                        <div className="min-w-[220px]">
                            <label className="block text-xs font-medium mb-1">Department</label>
                            <Select
                                allowClear
                                value={departmentId}
                                onChange={(v) => setDepartmentId(v ?? null)}
                                className="w-full"
                                placeholder="All departments"
                                options={departments.map((d) => ({
                                    value: d.id, label: d.department_name || d.name,
                                }))}
                            />
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="text-center py-12 text-muted-foreground">Loading…</div>
                ) : !data ? (
                    <div className="text-center py-12 text-muted-foreground">
                        Pick a cycle to see the grid.
                    </div>
                ) : plotted.length === 0 && awaiting.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                        Nothing to plot yet. Appraisals appear here once they have a final score.
                    </div>
                ) : (
                    <NineBoxGrid plotted={plotted} awaitingPotential={awaiting} />
                )}
            </div>
        </ContainerCard>
    );
};

export default NineBoxView;
